import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ChevronDown, 
  User, 
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  Crown,
  Bell,
  Sparkles,
  Zap,
  Shield,
  TrendingUp,
  Sun,
  Moon
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { languageCodes, defaultLanguage } from '../i18n';

// Navigation items. FX Astra portal pages are hidden — Navbar renders only on
// small auth/legal routes now, so we intentionally keep this list empty.
const getNavItems = (_t) => [];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t, i18n } = useTranslation();
  
  const navItems = getNavItems(t);

  // Get localized path helper
  const getLocalizedPath = (path) => {
    if (i18n.language === defaultLanguage) {
      return path;
    }
    return `/${i18n.language}${path === '/' ? '' : path}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to load user from localStorage
  const loadUserFromStorage = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('fx_token');
    const userData = localStorage.getItem('user') || localStorage.getItem('fx_user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    loadUserFromStorage();
  }, [location]);

  // Listen for storage changes (for when profile is updated in dashboard)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'fx_user' || e.key === 'user') {
        loadUserFromStorage();
      }
    };
    
    // Also listen for custom event from same window
    const handleUserUpdate = () => {
      loadUserFromStorage();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('fx_token');
    localStorage.removeItem('fx_user');
    setUser(null);
    navigate('/');
  };

  // Check if path is active (handles language prefixes)
  const isActive = (path) => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const isLangPrefix = pathParts[0] && languageCodes.includes(pathParts[0]);
    const currentPath = isLangPrefix ? '/' + pathParts.slice(1).join('/') : location.pathname;
    
    if (path === '/') {
      return currentPath === '/' || currentPath === '';
    }
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  return (
    <header
      data-testid="navbar"
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isDark
          ? isScrolled 
            ? 'bg-[#0f0f14]/95 backdrop-blur-xl shadow-lg shadow-black/20 border-b border-gray-800/50' 
            : 'bg-[#0f0f14]/80 backdrop-blur-md border-b border-transparent'
          : 'bg-gradient-to-r from-red-700 via-orange-700 to-orange-600',
        !isDark && isScrolled && 'shadow-lg shadow-orange-500/20'
      )}
    >
      <div className="container-app">
        <nav className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center shrink-0"
          >
            <img 
              src="/assets/astra-logo-full.png"
              alt="AstraTerminal" 
              className="h-8 w-auto object-contain"
              style={{ minWidth: '120px', maxWidth: '160px' }}
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-4">
            <div className={cn(
              "flex items-center gap-0.5 rounded-full px-1.5 py-0.5",
              isDark ? "bg-white/5" : "bg-white/10"
            )}>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={getLocalizedPath(item.path)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={cn(
                    'px-2.5 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap flex items-center gap-1',
                    isActive(item.path)
                      ? isDark 
                        ? 'text-white bg-gradient-to-r from-purple-600 to-purple-500 shadow-md shadow-purple-500/20'
                        : 'text-orange-600 bg-white shadow-md'
                      : item.highlight
                      ? isDark
                        ? 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'
                        : 'text-yellow-200 hover:text-white hover:bg-white/10'
                      : item.icon
                      ? isDark 
                        ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
                        : 'text-white hover:bg-white/10'
                      : isDark
                        ? 'text-gray-400 hover:text-white hover:bg-white/5'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                  )}
                >
                  {item.icon && <Zap className="h-3 w-3" />}
                  {item.highlight && <Sparkles className="h-3 w-3" />}
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Theme Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={cn(
                "h-8 w-8 rounded-full transition-all",
                isDark 
                  ? "text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10" 
                  : "text-white/80 hover:text-white hover:bg-white/10"
              )}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* User Menu / Auth Buttons */}
            {user ? (
              <>
                {/* Notifications */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "relative h-9 w-9 rounded-full",
                    isDark 
                      ? "text-gray-400 hover:text-white hover:bg-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  <Bell className="h-4 w-4" />
                  <span className={cn(
                    "absolute top-1.5 right-1.5 w-2 h-2 rounded-full ring-2",
                    isDark ? "bg-red-500 ring-[#0f0f14]" : "bg-yellow-300 ring-orange-500"
                  )} />
                </Button>

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      data-testid="user-menu-trigger"
                      className={cn(
                        "flex items-center gap-2.5 h-10 pl-1.5 pr-3 rounded-full transition-all",
                        isDark 
                          ? "bg-white/5 hover:bg-white/10 border border-gray-800 hover:border-gray-700"
                          : "bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30"
                      )}
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className={cn("w-7 h-7 rounded-full object-cover ring-2", isDark ? "ring-purple-500/50" : "ring-white/50")} />
                      ) : (
                        <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", isDark ? "bg-gradient-to-br from-purple-500 to-purple-600" : "bg-white")}>
                          <span className={cn("text-xs font-bold", isDark ? "text-white" : "text-orange-600")}>{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <span className={cn("hidden sm:inline text-sm font-medium", isDark ? "text-white" : "text-white")}>
                        {user.name?.split(' ')[0] || user.email?.split('@')[0] || 'User'}
                      </span>
                      {user.is_premium && (
                        <Crown className={cn("h-3.5 w-3.5", isDark ? "text-amber-400" : "text-yellow-300")} />
                      )}
                      <ChevronDown className={cn(
                        "h-3.5 w-3.5",
                        isDark ? "text-gray-500" : "text-gray-400"
                      )} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className={cn(
                      "w-56",
                      isDark 
                        ? "bg-[#1a1a24] border-gray-800 text-white"
                        : "bg-white border-gray-200 text-gray-900"
                    )}
                  >
                    <div className={cn(
                      "px-3 py-3 border-b",
                      isDark ? "border-gray-800" : "border-gray-200"
                    )}>
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white font-bold">{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        <div>
                          <p className={cn(
                            "font-semibold text-sm",
                            isDark ? "text-white" : "text-gray-900"
                          )}>{user.name || user.email?.split('@')[0] || 'User'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      {user.is_premium && (
                        <div className="mt-2 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs flex items-center gap-1.5">
                          <Crown className="h-3 w-3" /> Premium Member
                        </div>
                      )}
                    </div>
                    <div className="py-1">
                      <DropdownMenuItem asChild className={cn(
                        "cursor-pointer",
                        isDark ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      )}>
                        <Link to={getLocalizedPath("/dashboard")}>
                          <LayoutDashboard className={cn("h-4 w-4 mr-2", isDark ? "text-gray-500" : "text-gray-400")} />
                          {t('nav.dashboard', 'Dashboard')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className={cn(
                        "cursor-pointer",
                        isDark ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      )}>
                        <Link to={getLocalizedPath(`/traders/${user.user_id}`)}>
                          <User className={cn("h-4 w-4 mr-2", isDark ? "text-gray-500" : "text-gray-400")} />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      {!user.is_premium && (
                        <DropdownMenuItem asChild className="text-amber-500 hover:text-amber-600 hover:bg-amber-500/10 cursor-pointer">
                          <Link to={getLocalizedPath("/dashboard?tab=subscription")}>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Upgrade to Pro
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </div>
                    <DropdownMenuSeparator className={isDark ? "bg-gray-800" : "bg-gray-200"} />
                    <div className="py-1">
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10 cursor-pointer"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t('nav.logout', 'Sign Out')}
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  asChild
                  data-testid="login-btn"
                  className={cn(
                    "rounded-full px-3 h-8 text-xs",
                    isDark 
                      ? "text-gray-400 hover:text-white hover:bg-white/10"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  )}
                >
                  <Link to={getLocalizedPath("/auth")}>{t('nav.login', 'Sign In')}</Link>
                </Button>
                <Button
                  asChild
                  data-testid="register-btn"
                  className={cn(
                    "rounded-full px-4 h-8 text-xs shadow-lg",
                    isDark 
                      ? "bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white shadow-purple-500/25"
                      : "bg-white hover:bg-gray-50 text-orange-600 font-semibold"
                  )}
                >
                  <Link to={getLocalizedPath("/auth?tab=register")}>{t('auth.sign_up', 'Get Started')}</Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "lg:hidden h-9 w-9 rounded-full",
                isDark 
                  ? "text-gray-400 hover:text-white hover:bg-white/10"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              )}
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-trigger"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className={cn(
            "lg:hidden fixed inset-0 z-[100]",
            isDark ? "bg-[#0a0a0f]" : "bg-white"
          )}>
            {/* Mobile Header */}
            <div className={cn(
              "flex items-center justify-between px-4 h-16 border-b",
              isDark 
                ? "border-gray-800 bg-[#0a0a0f]"
                : "bg-gradient-to-r from-red-700 via-orange-700 to-orange-600 border-orange-600"
            )}>
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
                <img src="/assets/astra-logo-full.png" alt="AstraTerminal" className="h-8 w-auto" />
              </Link>
              <button 
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "p-2 rounded-full",
                  isDark 
                    ? "text-gray-400 hover:text-white hover:bg-white/10"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                )}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Mobile Nav Content */}
            <div className={cn(
              "overflow-y-auto h-[calc(100vh-64px)] p-4 pb-24",
              isDark ? "bg-[#0a0a0f]" : "bg-white"
            )}>
              {/* Main Navigation */}
              <div className="space-y-1 mb-6">
                <p className={cn(
                  "text-xs font-medium uppercase tracking-wider px-3 mb-2",
                  isDark ? "text-gray-500" : "text-gray-400"
                )}>Navigation</p>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={getLocalizedPath(item.path)}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors',
                      isActive(item.path)
                        ? 'text-white bg-purple-600/20 border border-purple-500/30'
                        : isDark
                          ? 'text-gray-300 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    {item.icon && <Zap className="h-5 w-5 text-purple-400" />}
                    {item.highlight && <Sparkles className="h-5 w-5 text-orange-400" />}
                    {!item.icon && !item.highlight && (
                      <div className={cn(
                        "w-5 h-5 rounded-md flex items-center justify-center",
                        isDark ? "bg-gray-800" : "bg-gray-200"
                      )}>
                        <span className={cn(
                          "text-[10px]",
                          isDark ? "text-gray-400" : "text-gray-500"
                        )}>{item.name.charAt(0)}</span>
                      </div>
                    )}
                    <span className="font-medium">{item.name}</span>
                    {item.highlight && (
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400">NEW</span>
                    )}
                  </Link>
                ))}
              </div>
              
              {/* Quick Links */}
              <div className="space-y-1 mb-6">
                <p className={cn(
                  "text-xs font-medium uppercase tracking-wider px-3 mb-2",
                  isDark ? "text-gray-500" : "text-gray-400"
                )}>Quick Links</p>
                <Link
                  to={getLocalizedPath("/brokers?filter=regulated")}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl",
                    isDark ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <Shield className="h-5 w-5 text-green-400" />
                  <span>Regulated Brokers</span>
                </Link>
                <Link
                  to={getLocalizedPath("/brokers?filter=low-spread")}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl",
                    isDark ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <TrendingUp className="h-5 w-5 text-blue-400" />
                  <span>Low Spread Brokers</span>
                </Link>
              </div>
              
              {/* User Section */}
              <div className={cn("border-t pt-4", isDark ? "border-gray-800" : "border-gray-200")}>
                {user ? (
                  <div className="space-y-1">
                    <p className={cn(
                      "text-xs font-medium uppercase tracking-wider px-3 mb-2",
                      isDark ? "text-gray-500" : "text-gray-400"
                    )}>Account</p>
                    <Link
                      to={getLocalizedPath("/dashboard")}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3.5 rounded-xl",
                        isDark ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      )}
                    >
                      <LayoutDashboard className="h-5 w-5 text-purple-400" />
                      <span>{t('nav.dashboard', 'Dashboard')}</span>
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-red-400 hover:bg-red-500/10"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>{t('nav.logout', 'Sign Out')}</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button variant="outline" asChild className={cn(
                      "w-full rounded-xl h-12",
                      isDark ? "border-gray-700 text-gray-300 hover:bg-white/5" : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    )} onClick={() => setMobileOpen(false)}>
                      <Link to={getLocalizedPath("/auth")}>{t('nav.login', 'Sign In')}</Link>
                    </Button>
                    <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 rounded-xl h-12 text-white" onClick={() => setMobileOpen(false)}>
                      <Link to={getLocalizedPath("/auth?tab=register")}>{t('nav.register', 'Create Account')}</Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
